import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  TextareaAutosize,
  IconButton,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs from "dayjs";
import config from "../../../aws-exports";
import "../../../styles/general.css";
import "../../../styles/amtpl.css";

const TabPanel = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

// Helper function to generate S3 URL
const getS3Url = (filePath) => {
  if (!filePath) return null;
  const bucketName = config.aws_user_files_s3_bucket;
  const region = config.aws_user_files_s3_bucket_region;
  return `https://${bucketName}.s3.${region}.amazonaws.com/public/${filePath}`;
};

// Helper function to get filename from path
const getFileName = (filePath) => {
  if (!filePath) return "";
  return filePath.split("/").pop();
};

// Tab 1: RFQ Information
const RFQInformationTab = ({ rfqData, productInfo }) => {
  return (
    <div className="row g-3">
      <div className="col-md-6">
        <Typography variant="body2">RFQ Title *</Typography>
        <TextField
          value={rfqData.Title || rfqData.RequirementNumber || ""}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Buyer/Trader Name</Typography>
        <TextField
          value={rfqData.BuyerEmailID?.trim()
            ? rfqData.BuyerEmailID
            : rfqData.GPEmailID?.trim()
            ? rfqData.GPEmailID
            : ""}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Creation Date</Typography>
        <TextField
          value={
            rfqData.RequirementDate
              ? dayjs(rfqData.RequirementDate).format("DD/MM/YYYY")
              : ""
          }
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Submission Date</Typography>
        <TextField
          value={
            rfqData.SubmitedDate
              ? dayjs(rfqData.SubmitedDate).format("DD/MM/YYYY")
              : ""
          }
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">RFQ Expiry Date/Time *</Typography>
        <TextField
          value={
            rfqData.ValidUntil
              ? dayjs(rfqData.ValidUntil).format("DD/MM/YYYY")
              : ""
          }
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
    </div>
  );
};

// Tab 2: Commodity Details
const CommodityDetailsTab = ({ rfqData, productInfo }) => {
  return (
    <div className="row g-3">
      <div className="col-md-6">
        <Typography variant="body2">Commodity Category *</Typography>
        <TextField
          value={rfqData?.CategoryName || ""}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Commodity/Product Name *</Typography>
        <TextField
          value={productInfo?.ProductName || ""}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">HS Code</Typography>
        <TextField
          value={rfqData.HsCode || ""}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Grade/Quality *</Typography>
        <TextField
          value={productInfo?.Grade || ""}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Variety</Typography>
        <TextField
          value={rfqData.Variety || ""}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Product Specifications</Typography>
        <TextareaAutosize
          value={rfqData.Description || rfqData.ProductSpecifications || ""}
          style={{
            backgroundColor: "white",
            width: "100%",
            minHeight: "56px",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontFamily: "inherit",
            fontSize: "14px",
            resize: "vertical",
          }}
          minRows={2}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Origin Preference</Typography>
        <TextField
          value={rfqData.OriginCountry || ""}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-12">
        <Typography variant="body2">Additional Product Notes</Typography>
        <TextareaAutosize
          value={
            rfqData.AdditionalSpecs || rfqData.AdditionalProductNotes || ""
          }
          style={{
            backgroundColor: "white",
            width: "100%",
            minHeight: "56px",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontFamily: "inherit",
            fontSize: "14px",
            resize: "vertical",
          }}
          minRows={2}
          disabled={true}
        />
      </div>
    </div>
  );
};

// Tab 3: Quantity & Packaging
const QuantityPackagingTab = ({ rfqData }) => {
  // Parse packaging information
  let packagingInfo = {};
  let productPackaging = "";
  let packageSize = "";

  if (rfqData.PackagingInfo) {
    try {
      packagingInfo = JSON.parse(rfqData.PackagingInfo);
      productPackaging = packagingInfo.PackagingType || "";
      packageSize =
        packagingInfo.PackageSize || packagingInfo.PackagingSize || "";
    } catch (e) {
      console.error("Error parsing packaging info:", e);
    }
  }

  // Parse ProductPackaging if it's in format {item=bigBag, size=1000kg-big-bag}
  if (rfqData.ProductPackaging && !productPackaging) {
    const packagingStr = rfqData.ProductPackaging;
    if (packagingStr.includes("{") && packagingStr.includes("}")) {
      try {
        // Remove curly braces and split by comma
        const cleaned = packagingStr.replace(/[{}]/g, "");
        const pairs = cleaned.split(",");

        pairs.forEach((pair) => {
          const [key, value] = pair.split("=").map((s) => s.trim());
          if (key === "item") {
            productPackaging = value;
          } else if (key === "size") {
            packageSize = value;
          }
        });
      } catch (e) {
        console.error("Error parsing ProductPackaging:", e);
        productPackaging = packagingStr;
      }
    } else {
      productPackaging = packagingStr;
    }
  }

  return (
    <div className="row g-3">
      <div className="col-md-4">
        <Typography variant="body2">Requested Quantity (MT) *</Typography>
        <TextField
          value={rfqData.Quantity ? `${rfqData.Quantity}` : ""}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Product Packaging</Typography>
        <TextField
          value={productPackaging}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Package Size</Typography>
        <TextField
          value={packageSize}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-12">
        <Typography variant="body2">Additional Packaging Notes</Typography>
        <TextareaAutosize
          value={
            packagingInfo.AdditionalPackagingNotes ||
            rfqData.PackagingNotes ||
            ""
          }
          style={{
            backgroundColor: "white",
            width: "100%",
            minHeight: "56px",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontFamily: "inherit",
            fontSize: "14px",
            resize: "vertical",
          }}
          minRows={2}
          disabled={true}
        />
      </div>
    </div>
  );
};

// Tab 4: Delivery Terms
const DeliveryTermsTab = ({ rfqData }) => {
  return (
    <div className="row g-3">
      <div className="col-md-6">
        <Typography variant="body2">Destination Country *</Typography>
        <TextField
          value={
            rfqData.DeliveryLocation?.split("~")[0] ||
            rfqData.DestinationCountry ||
            ""
          }
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Destination Port/Location *</Typography>
        <TextField
          value={
            rfqData.DeliveryLocation?.split("~")[1] ||
            rfqData.DeliveryLocation ||
            ""
          }
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Delivery Terms/Incoterms *</Typography>
        <TextField
          value={rfqData.Incoterm || rfqData.DeliveryTerms || ""}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Shipping Method Preference</Typography>
        <TextField
          value={
            rfqData.ShippingMethod || rfqData.ShippingMethodPreference || ""
          }
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Delivery Period From</Typography>
        <TextField
          value={
            rfqData.DeliveryPeriodFrom
              ? dayjs(rfqData.DeliveryPeriodFrom).format("DD/MM/YYYY")
              : ""
          }
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Delivery Period Till</Typography>
        <TextField
          value={
            rfqData.DeliveryPeriodTill
              ? dayjs(rfqData.DeliveryPeriodTill).format("DD/MM/YYYY")
              : ""
          }
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Partial Delivery Allowed</Typography>
        <TextField
          value={rfqData.PartialDeliveryAllowed === 1 ? "Yes" : "No"}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Transshipment Allowed</Typography>
        <TextField
          value={rfqData.TransshipmentAllowed === 1 ? "Yes" : "No"}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Unloading Responsibility</Typography>
        <TextField
          value={rfqData.UnloadingResponsibility || ""}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-12">
        <Typography variant="body2">Additional Logistics Notes</Typography>
        <TextareaAutosize
          value={
            rfqData.AdditionalLogisticsNotes || rfqData.LogisticsNotes || ""
          }
          style={{
            backgroundColor: "white",
            width: "100%",
            minHeight: "56px",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontFamily: "inherit",
            fontSize: "14px",
            resize: "vertical",
          }}
          minRows={2}
          disabled={true}
        />
      </div>
    </div>
  );
};

// Tab 5: Pricing/Payment/Guarantee
const PricingPaymentTab = ({ rfqData, productInfo }) => {
  return (
    <div className="row g-3">
      <div className="col-md-6">
        <Typography variant="body2">Price Offered (per MT) *</Typography>
        <TextField
          value={
            rfqData.PriceOffered
              ? `${rfqData.PriceOffered} ${
                  rfqData.PriceUnit || rfqData.Currency || "USD"
                }`
              : ""
          }
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Currency</Typography>
        <TextField
          value={rfqData.Currency || rfqData.PriceUnit || "USD"}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Preferred Payment Method</Typography>
        <TextField
          value={rfqData.PaymentTerms || rfqData.PreferredPaymentMethod || ""}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Payment Timeline</Typography>
        <TextField
          value={productInfo.TransitTime || ""}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Quality Inspection Required</Typography>
        <TextField
          value={rfqData.QualityInspectionRequired === 1 ? "Yes" : "No"}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">
          Required Supplier Certifications
        </Typography>
        <TextField
          value={rfqData.RequiredSupplierCertifications === 1 ? "Yes" : "No"}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-12">
        <Typography variant="body2">
          Special Instructions for Suppliers
        </Typography>
        <TextareaAutosize
          value={rfqData.SpecialInstructionsSuppliers || rfqData.Comments || ""}
          style={{
            backgroundColor: "white",
            width: "100%",
            minHeight: "56px",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontFamily: "inherit",
            fontSize: "14px",
            resize: "vertical",
          }}
          minRows={2}
          disabled={true}
        />
      </div>
      <div className="col-md-12">
        <Typography variant="body2">Comparison Criteria Priority</Typography>
        <TextareaAutosize
          value={rfqData.ComparisonCriteriaPriority || ""}
          style={{
            backgroundColor: "white",
            width: "100%",
            minHeight: "56px",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontFamily: "inherit",
            fontSize: "14px",
            resize: "vertical",
          }}
          minRows={2}
          disabled={true}
        />
      </div>
    </div>
  );
};

// Tab 6: Others
const OthersTab = ({ rfqData }) => {
  // Parse terms and conditions files
  const parseDocuments = (documentsStr) => {
    if (!documentsStr) return [];
    try {
      // Check if it's JSON array
      if (documentsStr.startsWith("[")) {
        const parsed = JSON.parse(documentsStr);
        return Array.isArray(parsed) ? parsed : [];
      }
      // If it's a single file path
      return [documentsStr];
    } catch (e) {
      // If not JSON, treat as single file path
      return documentsStr ? [documentsStr] : [];
    }
  };

  const termsConditionsFiles = parseDocuments(rfqData.TermsConditionsFilePath);
  const otherFiles = parseDocuments(rfqData.OthersFilePath);

  const renderDocumentList = (files, label) => {
    if (!files || files.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          No {label.toLowerCase()} uploaded
        </Typography>
      );
    }

    return files.map((filePath, index) => {
      const fileName = getFileName(filePath);
      const fileUrl = getS3Url(filePath);

      return (
        <Box
          key={index}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 8px",
            marginBottom: "4px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PictureAsPdfIcon sx={{ marginRight: 1, color: "#d32f2f" }} />
            <Typography variant="body2">{fileName}</Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => window.open(fileUrl, "_blank")}
            sx={{ color: "#de6b14" }}
            title="Download file"
          >
            <DownloadIcon />
          </IconButton>
        </Box>
      );
    });
  };

  return (
    <div className="row g-3">
      <div className="col-md-12">
        <Typography variant="body2">Internal Comments</Typography>
        <TextareaAutosize
          value={
            rfqData.InternalComments ||
            rfqData.AdditionalComments ||
            rfqData.Comments ||
            ""
          }
          style={{
            backgroundColor: "white",
            width: "100%",
            minHeight: "56px",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontFamily: "inherit",
            fontSize: "14px",
            resize: "vertical",
          }}
          minRows={3}
          disabled={true}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Terms & Conditions</Typography>
        <Box
          sx={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "8px",
            minHeight: "120px",
            backgroundColor: "white",
          }}
        >
          {renderDocumentList(termsConditionsFiles, "Terms & Conditions")}
        </Box>
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Other Documents</Typography>
        <Box
          sx={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "8px",
            minHeight: "120px",
            backgroundColor: "white",
          }}
        >
          {renderDocumentList(otherFiles, "Other Documents")}
        </Box>
      </div>
    </div>
  );
};

const RFQDetailsView = ({ rfqData, allProductMaster, userType }) => {
  const [tabValue, setTabValue] = useState(0);

  // Find product information based on ProductID
  const productInfo = allProductMaster?.find(
    (p) => p.ProductID === rfqData.ProductID
  );

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ width: "100%", bgcolor: "background.paper" }}>
      {/* Header */}
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "#de6b14" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="RFQ details tabs"
          sx={{
            "& .MuiTab-root": {
              color: "#000",
              textTransform: "none",
              backgroundColor: "transparent",
              "&.Mui-selected": {
                color: "#fff",
                backgroundColor: "#de6b14",
                borderBottom: "2px solid #d4d4d4",
              },
            },
            "& .MuiTabs-indicator": { backgroundColor: "#de6b14" },
          }}
        >
          <Tab label="RFQ Information" id="tab-0" aria-controls="tabpanel-0" />
          <Tab
            label="Commodity Details"
            id="tab-1"
            aria-controls="tabpanel-1"
          />
          <Tab
            label="Quantity & Packaging"
            id="tab-2"
            aria-controls="tabpanel-2"
          />
          <Tab label="Delivery Terms" id="tab-3" aria-controls="tabpanel-3" />
          <Tab
            label="Pricing/Payment/Guarantee"
            id="tab-4"
            aria-controls="tabpanel-4"
          />
          <Tab label="Others" id="tab-5" aria-controls="tabpanel-5" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <RFQInformationTab rfqData={rfqData} productInfo={productInfo} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <CommodityDetailsTab rfqData={rfqData} productInfo={productInfo} />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <QuantityPackagingTab rfqData={rfqData} />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <DeliveryTermsTab rfqData={rfqData} />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <PricingPaymentTab rfqData={rfqData} productInfo={productInfo} />
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <OthersTab rfqData={rfqData} />
      </TabPanel>
    </Box>
  );
};

export default RFQDetailsView;
